import { Observable, catchError, defer, from, switchMap, throwError } from 'rxjs';
import { hasUnauthorizedGraphQLError } from './graphql-auth-helpers';
import type { SessionRefreshOutcome } from './session-refresh';

/** A subscription's HTTP upgrade holds a snapshot of the session cookie.
 * Refreshing HTTP requests cannot update that socket. Renew the session and
 * replace the socket before retrying an auth refusal, once per subscription.
 * Permission refusals and network outages retain their original errors.
 */
export function recoverSubscriptionAuth<T>(
  source: () => Observable<T>,
  recover: () => Promise<SessionRefreshOutcome>,
): Observable<T> {
  return defer(source).pipe(catchError(cause => {
    if (!hasUnauthorizedGraphQLError(cause)) return throwError(() => cause);
    return from(recover()).pipe(switchMap(outcome =>
      outcome === 'refreshed' ? defer(source) : throwError(() => cause),
    ));
  }));
}

/** Coalesce refusals from all active feeds into one refresh and one restart. */
export function subscriptionCredentialRecovery(
  refresh: () => Promise<SessionRefreshOutcome>,
  restart: () => void,
): () => Promise<SessionRefreshOutcome> {
  let pending: Promise<SessionRefreshOutcome> | null = null;
  return () => pending ??= Promise.resolve().then(refresh).then(outcome => {
    if (outcome === 'refreshed') restart();
    return outcome;
  }).catch((): SessionRefreshOutcome => 'unavailable').finally(() => { pending = null; });
}
